// =====================================================================
// ImpressionsList.jsx - Lista jobs d'impression (M-003 Faza 2)
// =====================================================================
// Replica modulul Odoo "Printing report - Enregistrement job d'impression"
// (action-935). Modul COMPLET IZOLAT de Project/Timesheet.
//
// Permission gates:
//   - read.all -> vede toate jobs (COORDINATOR, SAV, FINANCE, ADMIN)
//   - read.own -> vede doar propriile jobs (OPERATOR_CLIENT cu override)
//   - create.own / create.all -> buton "Nouveau" vizibil
//   - update.own + same-day -> click pe rand propriu deschide form edit
//   - update.all -> click pe orice rand deschide form edit
//   - delete.all -> buton "Supprimer" in form edit (ADMIN+)
//   - invoice.all -> toggle "Facture" in coloana (ADMIN+)
//
// OPERATOR (fara perms printing_jobs.*) -> nici sidebar link, nici acces.
// OPERATOR_CLIENT primeste 0 perms la rol; doar Bianca/Aurelia/Razvan
// au override individual.
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  printingJobs as jobsApi,
  printingClients as clientsApi,
  printingTaskTemplates as templatesApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import ImpressionFormModal from './ImpressionFormModal.jsx';

const PAGE_SIZE = 80;

const COULEUR_LABELS_FR = {
  BW:    'N&B',
  COLOR: 'Couleur',
};

const PAPER_LABELS_FR = {
  A4: 'A4',
  A3: 'A3',
};

export default function ImpressionsList({ embedded = false }) {
  const { user, can } = useAuth();
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [totalPages, setTotalPgs] = useState(0); // sum aggregate (NU pagination)
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' | null

  // Dropdowns catalog
  const [clients, setClients]     = useState([]);
  const [templates, setTemplates] = useState([]);

  // Filtre
  const [search, setSearch]                 = useState('');
  const [filterInvoiced, setFilterInvoiced] = useState('all');
  const [filterClient, setFilterClient]     = useState('all');
  const [filterTemplate, setFilterTemplate] = useState('all');
  const [filterCouleur, setFilterCouleur]   = useState('all');
  const [filterPaper, setFilterPaper]       = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');

  // Modal state
  const [modalOpen, setModalOpen]   = useState(false);
  const [editingId, setEditingId]   = useState(null);

  // Permissions
  const canCreate    = can(['printing_jobs.create.own', 'printing_jobs.create.all']);
  const canReadAll   = can('printing_jobs.read.all');
  const canInvoice   = can('printing_jobs.invoice.all');

  // Load catalog dropdowns o singura data
  useEffect(() => {
    let cancelled = false;
    async function loadCatalogs() {
      try {
        const [cl, tpl] = await Promise.all([
          clientsApi.list({ isActive: true }),
          templatesApi.list({ isActive: true }),
        ]);
        if (cancelled) return;
        setClients(Array.isArray(cl) ? cl : (cl.items || []));
        setTemplates(Array.isArray(tpl) ? tpl : (tpl.items || []));
      } catch (err) {
        console.error('[ImpressionsList] catalog load error', err);
      }
    }
    loadCatalogs();
    return () => { cancelled = true; };
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (search.trim())              params.search = search.trim();
      if (filterInvoiced !== 'all')   params.invoiced = filterInvoiced;
      if (filterClient !== 'all')     params.clientId = filterClient;
      if (filterTemplate !== 'all')   params.taskTemplateId = filterTemplate;
      if (filterCouleur !== 'all')    params.couleur = filterCouleur;
      if (filterPaper !== 'all')      params.paperType = filterPaper;
      if (filterDateFrom)             params.dateFrom = filterDateFrom;
      if (filterDateTo)               params.dateTo   = filterDateTo;

      const res = await jobsApi.list(params);
      setItems(res.items || []);
      setTotal(res.total || 0);
      setTotalPgs(res.totalPages || 0);
    } catch (err) {
      console.error('[ImpressionsList] fetch error', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement');
      setItems([]);
      setTotal(0);
      setTotalPgs(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterInvoiced, filterClient, filterTemplate, filterCouleur, filterPaper, filterDateFrom, filterDateTo]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { setPage(1); }, [search, filterInvoiced, filterClient, filterTemplate, filterCouleur, filterPaper, filterDateFrom, filterDateTo]);

  const handleOpenCreate = () => { setEditingId(null); setModalOpen(true); };
  const handleOpenEdit = (jobId) => { setEditingId(jobId); setModalOpen(true); };
  const handleModalClose = () => { setModalOpen(false); setEditingId(null); };
  const handleModalSaved = () => { setModalOpen(false); setEditingId(null); fetchList(); };

  // Export handler (CSV sau XLSX) - foloseste filtre curente
  const handleExport = async (format) => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format });
      if (search)                           params.append('q', search);
      if (filterInvoiced !== 'all')         params.append('invoiced', filterInvoiced);
      if (filterClient !== 'all')           params.append('clientId', filterClient);
      if (filterTemplate !== 'all')         params.append('taskTemplateId', filterTemplate);
      if (filterCouleur !== 'all')          params.append('couleur', filterCouleur);
      if (filterPaper !== 'all')            params.append('paperType', filterPaper);
      if (filterDateFrom)                   params.append('dateFrom', filterDateFrom);
      if (filterDateTo)                     params.append('dateTo', filterDateTo);

      const response = await fetch(`/api/printing-jobs/export?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errData.message || "Erreur lors de l'export");
      }
      const cd = response.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename="([^"]+)"/);
      const filename = fnMatch ? fnMatch[1] : `impressions.${format}`;

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Impressions] export error', err);
      alert(err.message || "Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  const handleToggleInvoice = async (item, e) => {
    e.stopPropagation();
    if (!canInvoice) return;
    const confirmMsg = item.invoiced
      ? `Marquer ${item.ref} comme NON facturé ?`
      : `Marquer ${item.ref} comme facturé ?`;
    if (!confirm(confirmMsg)) return;
    try {
      await jobsApi.toggleInvoice(item.id, !item.invoiced);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du basculement');
    }
  };

  // Stats: sum pages BW vs COLOR pe pagina curenta (sau total daca disponibil)
  const pagesBW    = items.filter(i => i.couleur === 'BW').reduce((s, i) => s + (i.pages || 0), 0);
  const pagesColor = items.filter(i => i.couleur === 'COLOR').reduce((s, i) => s + (i.pages || 0), 0);
  const pagesTotal = pagesBW + pagesColor;

  const numPaginationPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstIdx   = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastIdx    = Math.min(page * PAGE_SIZE, total);

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const exportButtons = (
    <>
      <button
        onClick={() => handleExport('csv')}
        disabled={exporting !== null || loading || total === 0}
        style={exportBtnStyle('csv', exporting === 'csv')}
        title="Exporter en CSV (UTF-8 BOM, compatible Excel)"
      >
        {exporting === 'csv' ? '⏳ Export…' : `📥 CSV (${total})`}
      </button>
      <button
        onClick={() => handleExport('xlsx')}
        disabled={exporting !== null || loading || total === 0}
        style={exportBtnStyle('xlsx', exporting === 'xlsx')}
        title="Exporter en XLSX avec mise en forme"
      >
        {exporting === 'xlsx' ? '⏳ Export…' : '📊 XLSX'}
      </button>
    </>
  );

  return (
    <div style={{ padding: embedded ? 0 : 16, fontSize: 13 }}>
      {!embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22 }}>Jobs d'impression</h1>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>
              {canReadAll
                ? 'Tous les jobs d\'impression pour re-facturation client.'
                : 'Vos jobs d\'impression encodés.'}
            </p>
          </div>
          {canCreate && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {exportButtons}
              <button
                onClick={handleOpenCreate}
                style={{
                  padding: '8px 16px',
                  background: '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                + Nouveau
              </button>
            </div>
          )}
          {!canCreate && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {exportButtons}
            </div>
          )}
        </div>
      )}

      {/* Embedded mode: show "+ Nouveau" button standalone (no header context) */}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          {exportButtons}
          {canCreate && (
            <button
              onClick={handleOpenCreate}
              style={{
                padding: '8px 16px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              + Nouveau
            </button>
          )}
        </div>
      )}

      {/* Stats top: agregat pe pagina afișată */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 12,
        flexWrap: 'wrap',
      }}>
        <StatCard label="Total pages (page)" value={pagesTotal.toLocaleString('fr-BE')} color="#1e293b" />
        <StatCard label="N&B" value={pagesBW.toLocaleString('fr-BE')} color="#475569" />
        <StatCard label="Couleur" value={pagesColor.toLocaleString('fr-BE')} color="#0369a1" />
        <StatCard label="Total jobs (filtre)" value={total.toLocaleString('fr-BE')} color="#475569" />
        {totalPages > 0 && (
          <StatCard label="Pages totales (filtre)" value={totalPages.toLocaleString('fr-BE')} color="#1e293b" />
        )}
      </div>

      <div style={{
        display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
        padding: 12, background: '#f8fafc',
        border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 12,
      }}>
        <input
          type="text"
          placeholder="Rechercher (référence, commentaire...)"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 200px', padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        />
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Tous clients</option>
          {clients.sort((a, b) => a.label.localeCompare(b.label, 'fr')).map(c => (
            <option key={c.id} value={c.id}>{c.code} — {c.label}</option>
          ))}
        </select>
        <select value={filterTemplate} onChange={e => setFilterTemplate(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, maxWidth: 200 }}>
          <option value="all">Toutes tâches</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
        <select value={filterCouleur} onChange={e => setFilterCouleur(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Toutes couleurs</option>
          <option value="BW">N&B</option>
          <option value="COLOR">Couleur</option>
        </select>
        <select value={filterPaper} onChange={e => setFilterPaper(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Tous papiers</option>
          <option value="A4">A4</option>
          <option value="A3">A3</option>
        </select>
        <select value={filterInvoiced} onChange={e => setFilterInvoiced(e.target.value)}
                style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}>
          <option value="all">Tous (facturé)</option>
          <option value="false">Non facturé</option>
          <option value="true">Facturé</option>
        </select>
        <label style={{ fontSize: 12, color: '#64748b' }}>Du</label>
        <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)}
               style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        <label style={{ fontSize: 12, color: '#64748b' }}>Au</label>
        <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)}
               style={{ padding: 6, border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} />
        {(search || filterInvoiced !== 'all' || filterClient !== 'all' || filterTemplate !== 'all' ||
          filterCouleur !== 'all' || filterPaper !== 'all' || filterDateFrom || filterDateTo) && (
          <button onClick={() => {
            setSearch(''); setFilterInvoiced('all'); setFilterClient('all'); setFilterTemplate('all');
            setFilterCouleur('all'); setFilterPaper('all');
            setFilterDateFrom(''); setFilterDateTo('');
          }} style={{
            padding: '6px 10px', background: 'white', border: '1px solid #d1d5db',
            borderRadius: 4, fontSize: 12, cursor: 'pointer',
          }}>Effacer</button>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {loading ? 'Chargement...' : `${firstIdx}-${lastIdx} / ${total}`}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('csv', exporting === 'csv')}
            title="Exporter en CSV (UTF-8 BOM, compatible Excel)"
          >
            {exporting === 'csv' ? '⏳ Export…' : `📥 Exporter CSV (${total})`}
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || loading || total === 0}
            style={exportBtnStyle('xlsx', exporting === 'xlsx')}
            title="Exporter en XLSX avec mise en forme"
          >
            {exporting === 'xlsx' ? '⏳ Export…' : '📊 Exporter XLSX'}
          </button>
          <div style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          <button onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1 || loading} style={paginationBtnStyle(page <= 1)}>◀</button>
          <span style={{ fontSize: 12, padding: '0 8px' }}>Page {page} / {numPaginationPages}</span>
          <button onClick={() => setPage(p => Math.min(numPaginationPages, p + 1))}
                  disabled={page >= numPaginationPages || loading} style={paginationBtnStyle(page >= numPaginationPages)}>▶</button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: 12, background: '#fee2e2', color: '#991b1b',
          border: '1px solid #fca5a5', borderRadius: 4, marginBottom: 12,
        }}>{error}</div>
      )}

      <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', background: 'white' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>Tâche</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Pages</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Couleur</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Papier</th>
              <th style={thStyle}>Créé par</th>
              <th style={thStyle}>Référence</th>
              {canInvoice && <th style={{ ...thStyle, textAlign: 'center' }}>Facturé</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={canInvoice ? 9 : 8} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Chargement...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={canInvoice ? 9 : 8} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>
                Aucun job d'impression trouvé.
              </td></tr>
            ) : items.map(item => (
              <tr key={item.id}
                  onClick={() => handleOpenEdit(item.id)}
                  style={{
                    borderBottom: '1px solid #e2e8f0', cursor: 'pointer',
                    background: item.invoiced ? '#f0fdf4' : 'white',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = item.invoiced ? '#dcfce7' : '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = item.invoiced ? '#f0fdf4' : 'white'}>
                <td style={tdStyle}>{formatDate(item.date)}</td>
                <td style={tdStyle}>
                  <span style={{ fontWeight: 500 }}>{item.client?.code}</span>
                  <span style={{ color: '#64748b', marginLeft: 6, fontSize: 12 }}>
                    {item.client?.label}
                  </span>
                </td>
                <td style={tdStyle}>{item.taskTemplate?.label || '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {(item.pages || 0).toLocaleString('fr-BE')}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span style={couleurBadgeStyle(item.couleur)}>
                    {COULEUR_LABELS_FR[item.couleur] || item.couleur}
                  </span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <span style={paperBadgeStyle(item.paperType)}>
                    {PAPER_LABELS_FR[item.paperType] || item.paperType}
                  </span>
                </td>
                <td style={tdStyle}>{item.createdBy?.name || '—'}</td>
                <td style={{ ...tdStyle, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }}>
                  {item.ref}
                </td>
                {canInvoice && (
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={e => handleToggleInvoice(item, e)}
                            title={item.invoiced ? 'Marquer comme NON facturé' : 'Marquer comme facturé'}
                            style={{
                              padding: '2px 8px', fontSize: 11, fontWeight: 500,
                              border: '1px solid',
                              borderColor: item.invoiced ? '#86efac' : '#cbd5e1',
                              background: item.invoiced ? '#dcfce7' : 'white',
                              color: item.invoiced ? '#166534' : '#64748b',
                              borderRadius: 3, cursor: 'pointer',
                            }}>
                      {item.invoiced ? '✓ Facturé' : 'Non facturé'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <ImpressionFormModal
          jobId={editingId}
          onClose={handleModalClose}
          onSaved={handleModalSaved}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      padding: '8px 14px', background: 'white',
      border: '1px solid #e2e8f0', borderRadius: 6,
      minWidth: 110,
    }}>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

const thStyle = {
  padding: '10px 12px', textAlign: 'left', fontWeight: 500, fontSize: 12,
  color: '#475569', textTransform: 'uppercase', letterSpacing: 0.3,
};
const tdStyle = { padding: '10px 12px' };
const paginationBtnStyle = (disabled) => ({
  padding: '4px 10px', background: disabled ? '#f1f5f9' : 'white',
  border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13,
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? '#94a3b8' : '#1e293b',
});

const exportBtnStyle = (format, loading) => ({
  padding: '5px 10px',
  background: loading ? '#f1f5f9' : (format === 'xlsx' ? '#16a34a' : '#3b82f6'),
  color: loading ? '#94a3b8' : 'white',
  border: '1px solid ' + (loading ? '#cbd5e1' : (format === 'xlsx' ? '#15803d' : '#2563eb')),
  borderRadius: 4, fontSize: 12, fontWeight: 500,
  cursor: loading ? 'wait' : 'pointer',
});

const couleurBadgeStyle = (couleur) => {
  if (couleur === 'COLOR') {
    return {
      display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
      background: '#e0f2fe', color: '#075985', border: '1px solid #7dd3fc', borderRadius: 3,
    };
  }
  // BW (default)
  return {
    display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
    background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 3,
  };
};

const paperBadgeStyle = (paper) => {
  if (paper === 'A3') {
    return {
      display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
      background: '#fef3c7', color: '#78350f', border: '1px solid #fbbf24', borderRadius: 3,
    };
  }
  // A4 (default)
  return {
    display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 500,
    background: '#ecfdf5', color: '#065f46', border: '1px solid #6ee7b7', borderRadius: 3,
  };
};
